import React, {useMemo, useState} from "react";
import InfoIcon from "@mui/icons-material/Info";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {Avatar, CardContent, CardHeader, Menu, MenuItem, useTheme} from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import moment from "moment";
import {useTranslation} from "react-i18next";
import {useSelector} from "react-redux";
import {Link} from "react-router-dom";
import {getPackageInternalUrl} from "../../constants/appRoutes";
import AttachmentList from "../package-attachments/AttachmentList";
import PackageMetadataButton, {METADATA_BUTTON_VIEW_MODE_BUTTON} from "../package-metadata-button";
import SanitizedHTML from "../sanitized-html";
import SdmxDownloadButton from "./SdmxDownloadButton";
import {appConfigDateFormatSelector} from "../../state/app-config/appConfigSelectors";
import {hubNodesSelector} from "../../state/hub/hubSelectors";
import {
  CATALOG_RESOURCE_TYPE_METADATA_URL,
  getDatasetFromCatalog,
  getPackageExternalResources,
  getResourceFromCatalogByType
} from "../../utils/catalog";
import "./style.css";

const browseDataBtnStyle = {
  whiteSpace: "nowrap",
  padding: "8px",
  backgroundColor: "#00295a",
  color: "white",
  "&:hover": {
    backgroundColor: "#000030"
  }
};

const PackageCard = ({
  nodeId,
  catalog: externalCatalog,
  packageId,
  package: pkg,
  to,
  onClick,
  xs,
  md,
  isMultiCategorized,
  categoryItem,
  enableBrowseData = false,
  enableSDMXDownload = false,
  enableMetadataAsButton = false
}) => {
  const {t} = useTranslation();
  const theme = useTheme();

  const dateFormat = useSelector(appConfigDateFormatSelector);

  const nodes = useSelector(hubNodesSelector);

  // If I have an external catalog I use that otherwise I get the catalog from the state
  const catalog = useSelector(state => (externalCatalog ? externalCatalog : state.catalog));
  const categoryPathLabels = catalog.categoryPathLabels;

  const [detailsAnchorEl, setDetailsAnchorEl] = useState(null);
  const handleClick = event => {
    setDetailsAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setDetailsAnchorEl(null);
  };

  const associatedDataset = useMemo(() => getDatasetFromCatalog(packageId, catalog), [packageId, catalog]);
  const metadataResource = useMemo(
    () => getResourceFromCatalogByType(catalog, packageId, CATALOG_RESOURCE_TYPE_METADATA_URL),
    [packageId, catalog]
  );
  const externalResources = useMemo(() => getPackageExternalResources(packageId, catalog), [packageId, catalog]);

  const isBrowseDataVisible =
    categoryItem?.browseDataLink === "Internal" ||
    (categoryItem?.browseDataLink === "External" && associatedDataset?.externalDataUrl);

  const downloadFormats = JSON.parse(
    categoryItem?.nodeExtras?.find(({key}) => key === "DownloadFormats")?.value || "[]"
  );
  const sdmxAvailableFormats = downloadFormats.filter(v => v === "structurespecificdata" || v === "compactdata");
  const isDownloadSdmxVisible = categoryItem && categoryItem.enableSDMXDownload && sdmxAvailableFormats.length > 0;

  const isMetadataVisible =
    metadataResource !== null && metadataResource !== undefined && (!categoryItem || categoryItem.enableMetadata);

  const isCardHeaderActionsVisible = useMemo(() => {
    return isMultiCategorized || externalResources.length > 0;
  }, [isMultiCategorized, externalResources]);

  return (
    <Grid className={md === 4 ? "dataset-card__grid-mode" : "dataset-card__list-mode"} size={{xs: xs, md: md}}>
      <Card>
        <CardHeader
          className={"dataset-card__header"}
          avatar={
            pkg.source && (
              <Tooltip title={pkg.source}>
                <Avatar
                  className={pkg.source.replaceAll(" ", "").toLowerCase()}
                  sx={{backgroundColor: theme => theme.palette.secondary.main}}
                  aria-label="source"
                >
                  {pkg.source.replaceAll(" ", "").charAt(0).toUpperCase()}
                </Avatar>
              </Tooltip>
            )
          }
          action={
            isCardHeaderActionsVisible && (
              <>
                {isMultiCategorized && (
                  <Tooltip title={t("components.datasetCard.isMultiCategorized.title")}>
                    <Box display={"inline-flex"} sx={{verticalAlign: "middle"}}>
                      <InfoIcon fontSize="small" className={"dataset-card__actions__multicategorized-icon"} />
                    </Box>
                  </Tooltip>
                )}
                <IconButton
                  onClick={handleClick}
                  aria-label={t("components.datasetCard.header.actions.more.ariaLabel")}
                >
                  <Tooltip title={t("components.datasetCard.header.actions.more.title")}>
                    <MoreVertIcon />
                  </Tooltip>
                </IconButton>
                <Menu
                  id="dataset-card__header__actions-menu"
                  anchorEl={detailsAnchorEl}
                  open={!!detailsAnchorEl}
                  onClose={handleClose}
                  anchorOrigin={{
                    vertical: "top",
                    horizontal: "right"
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "right"
                  }}
                >
                  {externalResources.length > 0 && (
                    <MenuItem
                      disabled
                      sx={{
                        fontSize: 14,
                        fontStyle: "italic"
                      }}
                    >
                      {t("components.datasetCard.header.actions.more.attachments")}
                    </MenuItem>
                  )}
                  <AttachmentList packageId={packageId} attachments={externalResources} />
                </Menu>
              </>
            )
          }
          disableTypography
          title={
            to ? (
              <Link
                className={"dataset-card__header__title--link"}
                to={to}
                onClick={onClick}
                style={{
                  fontSize: 18,
                  color: theme.palette.text.primary
                }}
              >
                {pkg.title}
              </Link>
            ) : (
              <Box
                className={"dataset-card__header__title--button"}
                onClick={onClick}
                sx={{
                  fontSize: 18,
                  color: theme.palette.text.primary,
                  cursor: "pointer",
                  textDecoration: "none",
                  "&:hover": {
                    textDecoration: "underline"
                  }
                }}
              >
                {pkg.title}
              </Box>
            )
          }
          subheader={
            pkg.lastUpdate && (
              <Box
                className={"dataset-card__header__last-update"}
                sx={{
                  fontSize: 13,
                  color: "rgba(0, 0, 0, 0.54)"
                }}
              >
                {moment(pkg.lastUpdate).format(dateFormat)}
              </Box>
            )
          }
        />
        <CardContent
          className={"dataset-card__content"}
          sx={{
            padding: "0 16px 16px !important"
          }}
        >
          {(enableBrowseData || enableSDMXDownload || enableMetadataAsButton) && (
            <Box
              className={"dataset-card__buttons"}
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: "8px"
              }}
            >
              {enableBrowseData && (
                <Box
                  className={"dataset-card__buttons__browse-data"}
                  style={{visibility: isBrowseDataVisible ? "visible" : "hidden"}}
                >
                  {categoryItem?.browseDataLink === "Internal" ? (
                    <Link
                      to={getPackageInternalUrl(nodes.find(n => categoryItem.nodeId === n.nodeId)?.code, [], packageId)}
                    >
                      <Button
                        size="small"
                        sx={browseDataBtnStyle}
                        className="dataset-card__browse-data-btn"
                        color="primary"
                        variant="contained"
                      >
                        {t("components.datasetCard.buttons.browseData")}
                      </Button>
                    </Link>
                  ) : categoryItem?.browseDataLink === "External" && associatedDataset?.externalDataUrl ? (
                    <Button
                      size="small"
                      sx={browseDataBtnStyle}
                      className="dataset-card__browse-data-btn"
                      variant="contained"
                      onClick={() => window.open(associatedDataset.externalDataUrl)}
                    >
                      {t("components.datasetCard.buttons.browseData")}
                    </Button>
                  ) : (
                    <Button
                      size="small"
                      sx={browseDataBtnStyle}
                      className="dataset-card__browse-data-btn"
                      variant="contained"
                    >
                      {t("components.datasetCard.buttons.browseData")}
                    </Button>
                  )}
                </Box>
              )}
              {enableSDMXDownload && (
                <Box
                  className={"dataset-card__buttons__sdmx-download"}
                  style={{visibility: isDownloadSdmxVisible ? "visible" : "hidden"}}
                >
                  <SdmxDownloadButton
                    nodeId={categoryItem.nodeId}
                    packageId={packageId}
                    datasetId={packageId}
                    datasetTitle={pkg.title}
                    availableFormats={sdmxAvailableFormats}
                  />
                </Box>
              )}
              {enableMetadataAsButton && (
                <Box
                  className={"dataset-card__buttons__metadata"}
                  style={{visibility: isMetadataVisible ? "visible" : "hidden"}}
                >
                  <PackageMetadataButton
                    viewMode={METADATA_BUTTON_VIEW_MODE_BUTTON}
                    label={t("components.datasetCard.buttons.metadata")}
                    metadataUrl={associatedDataset?.metadataUrl}
                    packageId={packageId}
                    nodeId={nodeId}
                    catalog={catalog}
                    sx={{
                      backgroundColor: "#f8a81e",
                      color: "white",
                      "&:hover": {
                        backgroundColor: "#c07900"
                      }
                    }}
                  />
                </Box>
              )}
            </Box>
          )}
          {pkg.description && (
            <SanitizedHTML
              html={pkg.description}
              className={"dataset-card__description"}
              sx={{
                "& > p": {
                  margin: "0px"
                },
                fontSize: "14px",
                textAlign: "justify",
                color: "#6b6b6b",
                display: "-webkit-box",
                WebkitLineClamp: 3,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                textOverflow: "ellipsis"
              }}
            />
          )}
          {categoryPathLabels && (pkg.categoryPaths || []).length > 0 && (
            <Box
              className={"dataset-card__category"}
              sx={{
                fontSize: "14px",
                color: "#6b6b6b",
                display: "flex",
                gap: "4px",
                marginTop: "8px"
              }}
            >
              <Box className={"dataset-card__category_label"} sx={{fontWeight: "bold"}}>
                {t("components.datasetCard.category.label") + ":"}
              </Box>
              <Box className={"dataset-card__category__value"}>
                {(pkg.categoryPaths || []).map(path => categoryPathLabels[path]).join(" | ")}
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>
    </Grid>
  );
};

export default PackageCard;
